import { Cube, Drop, Image as ImageIcon, Plus, Wind, Warehouse, Lightning, Cylinder } from '@phosphor-icons/react';
import { chipZone, worstSeverity } from './helpers';
import w from './Workspace.module.css';
import { addArea, addEquipment, addObject, addSource, addSubstance, addUnderlay } from '../state/actions';
import { useDerived } from '../state/derived';
import { useStore, type Selection } from '../state/store';
import { formatNumber } from '../ui/format';
import { Button, StatusChip, ZoneSwatch } from '../ui/kit';

function Row({ sel, depth = 0, icon, label, meta, status }: { sel: NonNullable<Selection>; depth?: number; icon: React.ReactNode; label: string; meta?: React.ReactNode; status?: React.ReactNode }) {
  const selected = useStore((s) => s.selection?.id === sel.id);
  const select = useStore((s) => s.select);
  const requestFocus = useStore((s) => s.requestFocus);
  return (
    <li>
      <button type="button" className={w.row} aria-current={selected ? 'true' : undefined} style={{ paddingLeft: 10 + depth * 14 }}
        onClick={() => select(sel)} onDoubleClick={() => { select(sel); requestFocus(); }}>
        <span className={w.rowIcon}>{icon}</span>
        <span className={w.rowLabel}>{label}</span>
        {meta && <span className={w.rowMeta}>{meta}</span>}
        {status}
      </button>
    </li>
  );
}

export function ModelTree() {
  const doc = useStore((s) => s.doc);
  const derived = useDerived();
  if (!doc) return null;
  return (
    <nav className={w.tree} aria-label="Model">
      <div className={w.treeHead}>
        <span>Areas and sources</span>
        <span className={w.treeActions}>
          <Button variant="ghost" icon={<Plus size={14} />} title="Add a source of release (S)" aria-label="Add source of release" onClick={addSource} />
        </span>
      </div>
      <ul>
        {doc.areas.map((a) => {
          const sources = doc.sources.filter((s) => s.areaId === a.id);
          return (
            <li key={a.id}>
              <ul>
                <Row sel={{ kind: 'area', id: a.id }} icon={a.kind === 'indoor' ? <Warehouse size={14} /> : <Wind size={14} />} label={a.name} meta={`${sources.length}`} />
                {sources.map((s) => {
                  const r = derived?.results.sources[s.id];
                  const z = chipZone(r);
                  const sev = r ? worstSeverity(r.issues) : null;
                  return (
                    <Row key={s.id} depth={1} sel={{ kind: 'source', id: s.id }} icon={<Drop size={13} />} label={s.tag}
                      meta={z !== null ? <span className={w.zoneMeta}><ZoneSwatch zone={z} />{z === 'ne' ? 'NE' : `Z${z}`}</span> : undefined}
                      status={sev === 'error' ? <StatusChip status="error" label="" /> : sev === 'warning' ? <StatusChip status="warning" label="" /> : undefined} />
                  );
                })}
              </ul>
            </li>
          );
        })}
      </ul>
      <div className={w.addRow}>
        <Button variant="ghost" icon={<Plus size={13} />} onClick={() => addArea('indoor')}>Room</Button>
        <Button variant="ghost" icon={<Plus size={13} />} onClick={() => addArea('outdoor')}>Outdoor area</Button>
      </div>

      <div className={w.treeHead}>
        <span>Equipment</span>
        <Button variant="ghost" icon={<Plus size={14} />} title="Add equipment (E)" aria-label="Add equipment" onClick={addEquipment} />
      </div>
      <ul>
        {doc.equipment.map((e) => {
          const c = derived?.checks[e.id];
          return (
            <Row key={e.id} sel={{ kind: 'equipment', id: e.id }} icon={<Lightning size={13} />} label={e.tag}
              meta={e.ex.kind === 'ex' ? e.ex.epl : 'non-Ex'}
              status={c && (c.status === 'fail' || c.status === 'warning') ? <StatusChip status={c.status} label="" /> : undefined} />
          );
        })}
      </ul>

      <div className={w.treeHead}>
        <span>Substances</span>
        <Button variant="ghost" icon={<Plus size={14} />} aria-label="Add substance" onClick={addSubstance} />
      </div>
      <ul>
        {doc.substances.map((s) => <Row key={s.id} sel={{ kind: 'substance', id: s.id }} icon={<Drop size={13} weight="fill" />} label={s.name} meta={s.gasGroup} />)}
      </ul>

      <div className={w.treeHead}>
        <span>Site plan</span>
        <Button variant="ghost" icon={<Plus size={14} />} aria-label="Add a site plan image" title="Add a site plan image (PNG or JPEG)" onClick={() => {
          const input = document.createElement('input');
          input.type = 'file';
          input.accept = 'image/png,image/jpeg,image/webp';
          input.onchange = () => { const f = input.files?.[0]; if (f) void addUnderlay(f); };
          input.click();
        }} />
      </div>
      <ul>
        {(doc.underlays ?? []).map((u) => <Row key={u.id} sel={{ kind: 'underlay', id: u.id }} icon={<ImageIcon size={13} />} label={u.name} meta={u.visible ? `${formatNumber(u.widthM)} m` : 'hidden'} />)}
      </ul>
      {!(doc.underlays ?? []).length && <p className={w.treeHint}>Add a plan image to model over a real site.</p>}

      <div className={w.treeHead}><span>Plant geometry</span></div>
      <ul>
        {doc.objects.map((o) => <Row key={o.id} sel={{ kind: 'object', id: o.id }} icon={o.kind === 'cylinder' ? <Cylinder size={13} /> : <Cube size={13} />} label={o.name} />)}
      </ul>
      <div className={w.addRow}>
        <Button variant="ghost" icon={<Plus size={13} />} onClick={() => addObject('box')}>Box</Button>
        <Button variant="ghost" icon={<Plus size={13} />} onClick={() => addObject('cylinder')}>Vessel</Button>
      </div>
    </nav>
  );
}
